import { supabase } from "../../supabaseClient";
import { planDeal, summarizeBracketSquads } from "./constants";
import { clampTicketCount, matchCreatedBowlers } from "./entryTickets";

const throwIf = (error) => {
  if (error) throw error;
};

const productFields = (tournamentId, fields) => ({
  tournamentID: tournamentId,
  name: String(fields.name || "").trim(),
  productKind: fields.productKind || "traditional",
  scoringMode: fields.scoringMode,
  gameOrder: fields.gameOrder || [1, 2, 3],
  treeSize: 8,
  entryAmount: Number(fields.entryAmount),
  houseCut: Number(fields.houseCut) || 0,
  firstPlaceAmount: Number(fields.firstPlaceAmount),
  secondPlaceAmount: Number(fields.secondPlaceAmount),
  tieRule: fields.tieRule || "usbc_wc",
  sourceEventID: fields.sourceEventID || null,
});

const attachSquadRows = (products, squadRows) => {
  const byProduct = new Map();
  (squadRows || []).forEach((row) => {
    const list = byProduct.get(row.productID) || [];
    list.push(row);
    byProduct.set(row.productID, list);
  });
  return (products || []).map((product) => ({
    ...product,
    bracketProductSquad: byProduct.get(product.id) || [],
  }));
};

export const listBracketProducts = async (tournamentId) => {
  if (!tournamentId) return [];
  const { data, error } = await supabase
    .from("bracketProduct")
    .select("*")
    .eq("tournamentID", tournamentId)
    .eq("isDeleted", false)
    .order("createdAt", { ascending: true });
  throwIf(error);
  const products = data || [];
  if (!products.length) return products;
  const { data: squadRows, error: squadError } = await supabase
    .from("bracketProductSquad")
    .select("*")
    .in(
      "productID",
      products.map((product) => product.id)
    );
  if (squadError) return attachSquadRows(products, []);
  return attachSquadRows(products, squadRows);
};

export const tournamentHasBracketProducts = async (tournamentId) =>
  (await listBracketProducts(tournamentId)).length > 0;

export const saveBracketProduct = async (tournamentId, fields, productId) => {
  const row = {
    ...productFields(tournamentId, fields),
    updatedAt: new Date().toISOString(),
  };
  if (productId) {
    const { data, error } = await supabase
      .from("bracketProduct")
      .update(row)
      .eq("id", productId)
      .select()
      .single();
    throwIf(error);
    return data;
  }
  const { data, error } = await supabase
    .from("bracketProduct")
    .insert(row)
    .select()
    .single();
  throwIf(error);
  return data;
};

export const softDeleteBracketProduct = async (productId) => {
  const { error } = await supabase
    .from("bracketProduct")
    .update({
      isDeleted: true,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    .eq("id", productId);
  throwIf(error);
};

export const listSquadTickets = async (squadId, productIds) => {
  if (!squadId || !productIds?.length) return [];
  const { data, error } = await supabase
    .from("bracketTicket")
    .select("*")
    .eq("squadID", squadId)
    .in("productID", productIds)
    .order("createdAt", { ascending: true });
  throwIf(error);
  return data || [];
};

export const isSquadShuffled = async (productId, squadId) => {
  const { data, error } = await supabase
    .from("bracketProductSquad")
    .select("shuffledAt")
    .eq("productID", productId)
    .eq("squadID", squadId)
    .maybeSingle();
  if (error) return false;
  return Boolean(data?.shuffledAt);
};

export const loadBracketDesk = async (tournamentId, squadId, productId) => {
  const products = await listBracketProducts(tournamentId);
  const viewingAll = !squadId || squadId === "all";

  const [squadsResult, eventsResult, tournamentResult] = await Promise.all([
    supabase
      .from("squads")
      .select("id, squadDate, squadTime, description, isDeleted")
      .eq("tournamentID", tournamentId)
      .eq("isDeleted", false)
      .order("squadDate", { ascending: true })
      .order("squadTime", { ascending: true }),
    supabase
      .from("tournamentEvents")
      .select("id, eventName, isPrimary, isDeleted")
      .eq("tournamentID", tournamentId)
      .eq("isPrimary", true)
      .eq("isDeleted", false),
    supabase
      .from("tournaments")
      .select("id, handicapBase, handicapPercentage, handicapMax")
      .eq("id", tournamentId)
      .single(),
  ]);
  throwIf(squadsResult.error);
  throwIf(eventsResult.error);
  throwIf(tournamentResult.error);

  const squads = squadsResult.data || [];
  const enabled = products.filter(
    (product) => product && product.isDeleted !== true
  );
  const product =
    enabled.find((row) => row.id === productId) || enabled[0] || null;
  const selectedSquadIds = viewingAll
    ? squads.map((row) => row.id)
    : squads.filter((row) => row.id === squadId).map((row) => row.id);
  const allSquadIds = squads.map((row) => row.id);
  const productIds = enabled.map((row) => row.id);

  const [teamsResult, summaryTicketsResult] = await Promise.all([
    selectedSquadIds.length
      ? supabase
          .from("teams")
          .select(
            `
            id,
            teamName,
            squadID,
            eventsID,
            startingLane,
            startingPair,
            teamassignments (
              id,
              bowlerID,
              bowlers ( id, name )
            )
          `
          )
          .in("squadID", selectedSquadIds)
          .eq("isDeleted", false)
      : Promise.resolve({ data: [], error: null }),
    viewingAll && productIds.length && allSquadIds.length
      ? supabase
          .from("bracketTicket")
          .select("squadID, productID")
          .in("productID", productIds)
          .in("squadID", allSquadIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  throwIf(teamsResult.error);
  throwIf(summaryTicketsResult.error);

  const teams = teamsResult.data || [];
  const squadSummaries = viewingAll
    ? summarizeBracketSquads({
        squads,
        teams,
        tickets: summaryTicketsResult.data || [],
        products: enabled,
      })
    : [];
  const bowlerMap = new Map();
  teams.forEach((team) => {
    (team.teamassignments || []).forEach((assignment) => {
      const bowler = assignment.bowlers;
      if (!bowler?.id) return;
      if (product?.sourceEventID && team.eventsID !== product.sourceEventID) {
        return;
      }
      const existing = bowlerMap.get(bowler.id);
      bowlerMap.set(bowler.id, {
        id: bowler.id,
        name: bowler.name,
        lane: existing?.lane || team.startingLane || team.startingPair || null,
        teamID: team.id,
      });
    });
  });
  const bowlers = [...bowlerMap.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name))
  );

  if (!product) {
    return {
      products: enabled,
      allProducts: products,
      product: null,
      squads,
      squadSummaries,
      events: eventsResult.data || [],
      tournament: tournamentResult.data,
      bowlers,
      teams,
      tickets: [],
      trees: [],
      matches: [],
      payouts: [],
      scores: [],
      averages: [],
      squadState: null,
      shufflePlan: planDeal([]),
    };
  }

  const bowlerIds = bowlers.map((bowler) => bowler.id);
  const teamIds = teams
    .filter((team) =>
      product.sourceEventID ? team.eventsID === product.sourceEventID : true
    )
    .map((team) => team.id);
  const emptyRows = Promise.resolve({ data: [], error: null });
  const ticketsQuery = selectedSquadIds.length
    ? supabase
        .from("bracketTicket")
        .select("*")
        .eq("productID", product.id)
        .in("squadID", selectedSquadIds)
        .order("createdAt", { ascending: true })
    : emptyRows;
  const treesQuery = selectedSquadIds.length
    ? supabase
        .from("bracketTree")
        .select("*")
        .eq("productID", product.id)
        .in("squadID", selectedSquadIds)
        .order("treeNumber", { ascending: true })
    : emptyRows;
  const payoutsQuery = selectedSquadIds.length
    ? supabase
        .from("bracketPayout")
        .select("*")
        .eq("productID", product.id)
        .in("squadID", selectedSquadIds)
    : emptyRows;

  const [ticketsResult, treesResult, payoutsResult, scoresResult, averagesResult] =
    await Promise.all([
      ticketsQuery,
      treesQuery,
      payoutsQuery,
      teamIds.length
        ? supabase
            .from("scores")
            .select("id, bowlerID, gameNumber, score, teamID")
            .in("teamID", teamIds)
        : Promise.resolve({ data: [], error: null }),
      bowlerIds.length
        ? supabase
            .from("tournamentAverages")
            .select("bowlerID, average, hasAverage")
            .eq("tournamentID", tournamentId)
            .in("bowlerID", bowlerIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
  throwIf(ticketsResult.error);
  throwIf(treesResult.error);
  throwIf(payoutsResult.error);
  throwIf(scoresResult.error);
  throwIf(averagesResult.error);

  const trees = treesResult.data || [];
  const treeIds = trees.map((tree) => tree.id);
  const { data: matches, error: matchesError } = treeIds.length
    ? await supabase.from("bracketMatch").select("*").in("treeID", treeIds)
    : { data: [], error: null };
  throwIf(matchesError);

  const squadState = viewingAll
    ? null
    : (product.bracketProductSquad || []).find((row) => row.squadID === squadId) ||
      null;
  const tickets = ticketsResult.data || [];

  return {
    products: enabled,
    allProducts: products,
    product,
    squads: squadsResult.data || [],
    squadSummaries,
    events: eventsResult.data || [],
    tournament: tournamentResult.data,
    bowlers,
    teams,
    tickets,
    trees,
    matches: matches || [],
    payouts: payoutsResult.data || [],
    scores: scoresResult.data || [],
    averages: averagesResult.data || [],
    squadState,
    shufflePlan: viewingAll ? planDeal([]) : planDeal(tickets),
  };
};

export const setBowlerTicketCount = async ({
  productId,
  squadId,
  bowlerId,
  nextCount,
}) => {
  if (!productId || !squadId || !bowlerId) return;
  if (await isSquadShuffled(productId, squadId)) return;

  const { data: current, error: readError } = await supabase
    .from("bracketTicket")
    .select("id, createdAt")
    .eq("productID", productId)
    .eq("squadID", squadId)
    .eq("bowlerID", bowlerId)
    .order("createdAt", { ascending: true });
  throwIf(readError);

  const rows = current || [];
  const target = clampTicketCount(nextCount);
  if (target === rows.length) return;

  if (target > rows.length) {
    const add = target - rows.length;
    const insert = Array.from({ length: add }, () => ({
      productID: productId,
      squadID: squadId,
      bowlerID: bowlerId,
    }));
    const { error } = await supabase.from("bracketTicket").insert(insert);
    throwIf(error);
    return;
  }

  const removeIds = rows
    .slice()
    .reverse()
    .slice(0, rows.length - target)
    .map((row) => row.id);
  const { error } = await supabase
    .from("bracketTicket")
    .delete()
    .in("id", removeIds);
  throwIf(error);
};

export const applyBowlerTicketCounts = async ({
  squadId,
  bowlerId,
  counts,
}) => {
  if (!squadId || !bowlerId) return;
  for (const [productId, nextCount] of Object.entries(counts || {})) {
    await setBowlerTicketCount({
      productId,
      squadId,
      bowlerId,
      nextCount,
    });
  }
};

export const applyTicketsAfterTeamCreate = async ({
  teamId,
  squadId,
  formBowlers,
}) => {
  if (!squadId) return;
  let matched = (formBowlers || []).map((formBowler) => ({
    formBowler,
    bowlerId:
      formBowler?.existingBowler &&
      formBowler.id &&
      !String(formBowler.id).startsWith("temp-")
        ? formBowler.id
        : null,
  }));
  if (teamId) {
    const { data, error } = await supabase
      .from("teamassignments")
      .select("bowlerID, bowlers ( id, name )")
      .eq("teamID", teamId);
    if (!error) {
      matched = matchCreatedBowlers(data, formBowlers);
    }
  }
  for (const { formBowler, bowlerId } of matched) {
    await applyBowlerTicketCounts({
      squadId,
      bowlerId,
      counts: formBowler?.bracketTickets,
    });
  }
};

export const shuffleBracketSquad = async (productId, squadId) => {
  const { data, error } = await supabase.rpc("shuffle_bracket_squad", {
    p_product_id: productId,
    p_squad_id: squadId,
  });
  throwIf(error);
  return data;
};

export const unshuffleBracketSquad = async (productId, squadId) => {
  const { error } = await supabase.rpc("unshuffle_bracket_squad", {
    p_product_id: productId,
    p_squad_id: squadId,
  });
  throwIf(error);
};

export const upsertPayoutPaid = async (row, paid) => {
  const paidAt = paid ? new Date().toISOString() : null;
  if (row.payoutId) {
    const { error } = await supabase
      .from("bracketPayout")
      .update({ paidAt, amount: row.amount })
      .eq("id", row.payoutId);
    throwIf(error);
    return;
  }
  const { error } = await supabase.from("bracketPayout").insert({
    productID: row.productID,
    squadID: row.squadID,
    ticketID: row.ticket.id,
    treeID: row.treeID || null,
    kind: row.kind,
    amount: row.amount,
    paidAt,
  });
  throwIf(error);
};

export const setPayoutsPaid = async (rows, paid) => {
  await Promise.all((rows || []).map((row) => upsertPayoutPaid(row, paid)));
};

const listenTable = (channel, table, filter, onChange) =>
  channel.on(
    "postgres_changes",
    { event: "*", schema: "public", table, filter },
    onChange
  );

export const subscribeBracketProducts = (tournamentId, onChange) => {
  if (!tournamentId) return () => {};
  const channel = listenTable(
    supabase.channel(`bracket-products-${tournamentId}`),
    "bracketProduct",
    `tournamentID=eq.${tournamentId}`,
    onChange
  ).subscribe();
  return () => {
    channel.unsubscribe();
  };
};

export const subscribeBracketDesk = ({
  squadId,
  productId,
  onChange,
}) => {
  if (!productId) return () => {};
  const viewingAll = !squadId || squadId === "all";
  let channel = supabase.channel(
    viewingAll
      ? `brackets-desk-${productId}-all`
      : `brackets-desk-${productId}-${squadId}`
  );
  const maybeReload = (payload) => {
    const row = payload.new || payload.old;
    if (!viewingAll && row?.squadID && row.squadID !== squadId) return;
    if (row?.productID && row.productID !== productId) return;
    onChange("change");
  };
  channel = listenTable(
    channel,
    "bracketTicket",
    `productID=eq.${productId}`,
    maybeReload
  );
  channel = listenTable(
    channel,
    "bracketTree",
    `productID=eq.${productId}`,
    maybeReload
  );
  channel = listenTable(
    channel,
    "bracketPayout",
    `productID=eq.${productId}`,
    maybeReload
  );
  channel = listenTable(
    channel,
    "bracketProductSquad",
    `productID=eq.${productId}`,
    maybeReload
  );
  channel.subscribe();
  return () => {
    channel.unsubscribe();
  };
};
